import { createReducer } from "typesafe-actions";

import { convertGeoPointToLatLngLiteral } from "@mapmycustomers/shared/util/geo/GeoService";

import {
  Actions,
  addPinsToRoute,
  applyRouteViewSettings,
  clearRouteDetails,
  fetchPins,
  fetchRouteStopVisuals,
  fetchRouteViewPins,
  hideRouteAnnotation,
  initializeRouteView,
  insertStop,
  persistRouteViewport,
  setGoToLocationPopoverVisibility,
  setRouteFocusedStop,
  setRoutePinHover,
  setRouteSelectedPin,
  showRouteAnnotation,
  showRouteManualStopAnnotation,
} from "../../actions";
import seedStopVisuals, { StopVisualSeed } from "../../utils/seedStopVisuals";
import { initialMapState, RouteMapState } from "../state";

// Optimistic stop colors (see seedStopVisuals): seed a just-added stop's visual from
// the pins already on the map so it doesn't flash in the default color.
const withSeededStopVisuals = (state: RouteMapState, stops: StopVisualSeed[]): RouteMapState => {
  const stopVisuals = seedStopVisuals(
    state.stopVisuals,
    [...state.categorizedMapEntries.entities, ...state.categorizedExternalMapEntries.entities],
    stops
  );
  return stopVisuals === state.stopVisuals ? state : { ...state, stopVisuals };
};

export const pinsReducer = createReducer<RouteMapState, Actions>(initialMapState)
  .handleAction(initializeRouteView.request, (state) => ({
    ...state,
    annotation: undefined,
    categorizedExternalMapEntries: initialMapState.categorizedExternalMapEntries,
    categorizedMapEntries: initialMapState.categorizedMapEntries,
    lasso: initialMapState.lasso,
    pinHover: undefined,
    recordsList: { ...state.recordsList, multiPin: undefined },
    rulerPath: initialMapState.rulerPath,
    selected: initialMapState.selected,
    stopVisuals: initialMapState.stopVisuals,
    tool: undefined,
  }))
  .handleAction(clearRouteDetails, () => initialMapState)
  .handleAction(setGoToLocationPopoverVisibility, (state, { payload }) => ({
    ...state,
    goToLocationPopoverVisible: payload,
  }))
  .handleAction([fetchRouteViewPins.request, fetchPins.request], (state) => ({
    ...state,
    pinsLoading: true,
  }))
  .handleAction([fetchRouteViewPins.success, fetchPins.success], (state, { payload }) => ({
    ...state,
    categorizedExternalMapEntries: payload.externalEntries,
    categorizedMapEntries: {
      clusters: payload.clusters,
      entities: payload.entities,
      multiPins: payload.multiPins,
    },
    pinsCount: payload.pinsCount,
    pinsLoading: false,
  }))
  .handleAction([fetchRouteViewPins.failure, fetchPins.failure], (state) => ({
    ...state,
    pinsLoading: false,
  }))
  .handleAction(applyRouteViewSettings, (state, { payload }) => ({
    ...state,
    viewState: {
      ...state.viewState,
      boundaryKey: { ...(state.viewState.boundaryKey ?? {}), ...payload.boundaryKey },
      colorKey: { ...(state.viewState.colorKey ?? {}), ...payload.colorKey },
      columns: payload.columns ?? state.viewState.columns,
      filter: payload.filter
        ? {
            ...state.viewState.filter,
            ...payload.filter,
          }
        : state.viewState.filter,
      range: payload.range ?? state.viewState.range,
      // only update selectedSavedFilterId when it is explicitly present in a payload (even when it is `undefined`)
      selectedSavedFilterId:
        "selectedSavedFilterId" in payload
          ? payload.selectedSavedFilterId
          : state.viewState.selectedSavedFilterId,
      shapeKey: { ...(state.viewState.shapeKey ?? {}), ...payload.shapeKey },
      showOther: "showOther" in payload ? payload.showOther : state.viewState.showOther,
      sort: payload.sort ?? state.viewState.sort,
      visibleEntities: payload.visibleEntities ?? state.viewState.visibleEntities,
    },
  }))
  .handleAction(showRouteAnnotation, (state, { payload }) => ({
    ...state,
    annotation: { ...payload, type: "entity" },
  }))
  .handleAction(hideRouteAnnotation, (state) => ({
    ...state,
    annotation: undefined,
  }))
  .handleAction(showRouteManualStopAnnotation, (state, { payload }) => ({
    ...state,
    annotation: {
      address: payload.address,
      name: payload.name,
      position: convertGeoPointToLatLngLiteral(payload.geoPoint),
      type: "manual",
    },
  }))
  .handleAction(setRoutePinHover, (state, { payload }) =>
    state.recordsList.multiPin ? state : { ...state, pinHover: payload }
  )
  .handleAction(setRouteSelectedPin, (state, { payload }) => ({
    ...state,
    selectedPin: payload,
  }))
  .handleAction(persistRouteViewport, (state, { payload }) => ({
    ...state,
    viewport: payload,
  }))
  .handleAction(setRouteFocusedStop, (state, { payload }) => ({
    ...state,
    focusedStop: payload,
  }))
  .handleAction(addPinsToRoute, (state, { payload }) => withSeededStopVisuals(state, payload.pins))
  .handleAction(insertStop.success, (state, { payload }) =>
    withSeededStopVisuals(state, [{ id: payload.entity.id, entity: payload.entityType }])
  )
  .handleAction(fetchRouteStopVisuals.request, (state) => ({
    ...state,
    stopVisualsLoading: true,
  }))
  .handleAction(fetchRouteStopVisuals.success, (state, { payload }) => ({
    ...state,
    stopVisuals: payload.stopVisuals,
    stopVisualsLoading: false,
  }))
  .handleAction(fetchRouteStopVisuals.failure, (state) => ({
    ...state,
    stopVisualsLoading: false,
  }));

export default pinsReducer;
